// apps/frontend/src/contexts/UpsellContext.tsx
// Context global pour afficher la modale d'upsell depuis n'importe où.
// Utilisation type :
//   const { showUpsell, showUpsellFromResponse } = useUpsell();
//   const res = await fetch(...);
//   if (res.status === 402) {
//     const data = await res.json();
//     showUpsellFromResponse(data);
//     return;
//   }

import React, { createContext, useContext, useState, useCallback } from 'react';
import UpsellModal, { type UpsellInfo } from '../components/common/UpsellModal';

interface UpsellCtx {
  showUpsell: (info: UpsellInfo) => void;
  showUpsellFromResponse: (data: unknown) => boolean;
  hideUpsell: () => void;
}

const UpsellContext = createContext<UpsellCtx | null>(null);

export const UpsellProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [info, setInfo] = useState<UpsellInfo | null>(null);

  const showUpsell = useCallback((next: UpsellInfo) => {
    setInfo(next);
  }, []);

  const hideUpsell = useCallback(() => {
    setInfo(null);
  }, []);

  /**
   * Détecte si une réponse JSON ressemble à une 402 d'upsell et
   * affiche la modale en conséquence. Renvoie true si la modale a
   * été déclenchée (utile pour court-circuiter le toast d'erreur).
   */
  const showUpsellFromResponse = useCallback((data: unknown): boolean => {
    if (!data || typeof data !== 'object' || (data as any).success !== false) {
      return false;
    }
    const d = data as Record<string, unknown>;
    const upgradePlan = d.upgradePlan;
    const code = d.code;
    if (
      typeof upgradePlan !== 'string' ||
      (upgradePlan !== 'BASIC' && upgradePlan !== 'PRO')
    ) {
      return false;
    }
    const message =
      typeof d.message === 'string'
        ? d.message
        : 'Cette fonctionnalité est incluse dans nos plans payants.';
    setInfo({
      code: typeof code === 'string' ? code : 'PLAN_REQUIRED',
      message,
      upgradePlan,
      used: typeof d.used === 'number' ? d.used : undefined,
      limit: typeof d.limit === 'number' ? d.limit : undefined,
    });
    return true;
  }, []);

  return (
    <UpsellContext.Provider
      value={{ showUpsell, showUpsellFromResponse, hideUpsell }}
    >
      {children}
      <UpsellModal info={info} onClose={hideUpsell} />
    </UpsellContext.Provider>
  );
};

export const useUpsell = (): UpsellCtx => {
  const ctx = useContext(UpsellContext);
  if (!ctx) {
    // Fallback no-op si le provider n'est pas monté — évite un crash sur
    // les pages standalone (auth, 404, etc.) qui n'en ont pas besoin.
    return {
      showUpsell: () => {},
      showUpsellFromResponse: () => false,
      hideUpsell: () => {},
    };
  }
  return ctx;
};
